/**
 * SliderRangeEditor
 *
 * Property editor for slider-range control configuration
 *
 * Features:
 * - Minimum value configuration
 * - Maximum value configuration
 * - Step configuration
 * - Markers/ticks configuration
 * - Labels on slider
 * - Accessibility: ARIA labels, keyboard navigation
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface SliderRangeConfig {
  min?: number;
  max?: number;
  step?: number;
  marks?: Array<{ value: number; label: string }>;
  showLabels?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

function renderSliderRangeEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as SliderRangeConfig;

  const min = config.min ?? 0;
  const max = config.max ?? 100;
  const step = config.step ?? 1;
  const marks = config.marks || [];
  const showLabels = config.showLabels ?? false;
  const orientation = config.orientation ?? 'horizontal';

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('min', Number(e.currentTarget.value));
    },
    [onConfigChange],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('max', Number(e.currentTarget.value));
    },
    [onConfigChange],
  );

  const handleStepChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('step', Number(e.currentTarget.value));
    },
    [onConfigChange],
  );

  const handleAddMark = useCallback(() => {
    const newMarks = [...marks, { value: min + (max - min) / 2, label: `Mark ${marks.length + 1}` }];
    onConfigChange('marks', newMarks);
  }, [marks, min, max, onConfigChange]);

  const handleRemoveMark = useCallback(
    (index: number) => {
      onConfigChange('marks', marks.filter((_, i) => i !== index));
    },
    [marks, onConfigChange],
  );

  const handleUpdateMark = useCallback(
    (index: number, field: 'value' | 'label', val: string | number) => {
      const updated = marks.map((m, i) =>
        i === index ? { ...m, [field]: field === 'value' ? Number(val) : val } : m,
      );
      onConfigChange('marks', updated);
    },
    [marks, onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Slider Range Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="min-input" className="af-properties-tab__label">
          Minimum Value
        </label>
        <input
          id="min-input"
          type="number"
          value={min}
          onChange={handleMinChange}
          className="af-properties-tab__input"
          aria-label="Minimum value"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-input" className="af-properties-tab__label">
          Maximum Value
        </label>
        <input
          id="max-input"
          type="number"
          value={max}
          onChange={handleMaxChange}
          className="af-properties-tab__input"
          aria-label="Maximum value"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="step-input" className="af-properties-tab__label">
          Step Size
        </label>
        <input
          id="step-input"
          type="number"
          value={step}
          onChange={handleStepChange}
          min="0.01"
          step="0.01"
          className="af-properties-tab__input"
          aria-label="Step size"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="orientation-select" className="af-properties-tab__label">
          Orientation
        </label>
        <select
          id="orientation-select"
          value={orientation}
          onChange={(e) => onConfigChange('orientation', e.target.value)}
          className="af-properties-tab__select"
        >
          <option value="horizontal">Horizontal</option>
          <option value="vertical">Vertical</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={showLabels}
            onChange={(e) => onConfigChange('showLabels', e.target.checked)}
          />
          Show Value Labels
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>
          Marks/Ticks
        </label>
        {marks.length === 0 && <p className="af-properties-tab__info">No marks configured. Add one below.</p>}
        {marks.map((mark, i) => (
          <div key={i} className="af-properties-tab__mark-row">
            <input
              type="number"
              value={mark.value}
              onChange={(e) => handleUpdateMark(i, 'value', e.target.value)}
              min={min}
              max={max}
              className="af-properties-tab__input af-properties-tab__mark-value"
              placeholder="value"
              aria-label={`Mark ${i + 1} value`}
            />
            <input
              type="text"
              value={mark.label}
              onChange={(e) => handleUpdateMark(i, 'label', e.target.value)}
              className="af-properties-tab__input af-properties-tab__mark-label"
              placeholder="label"
              aria-label={`Mark ${i + 1} label`}
            />
            <button
              onClick={() => handleRemoveMark(i)}
              className="af-properties-tab__button af-properties-tab__button--danger"
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddMark}
          className="af-properties-tab__button af-properties-tab__button--primary"
          type="button"
        >
          Add Mark
        </button>
      </div>
    </div>
  );
}

export const SliderRangeEditor: PropertyEditor = {
  type: 'slider-range',
  label: 'Slider Range',
  description: 'Configure numeric range slider with min, max, step, and marks',
  render: renderSliderRangeEditor,
};
